import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Pause, Play } from "lucide-react";
import { useRadio } from "@/context/RadioContext";
import { Container } from "@/components/site/PageHeader";
import { SectionHeading, Label } from "@/components/site/SectionHeading";
import { ArticleList, PODCAST_FILTER } from "@/pages/Articles";
import { LatestEntries } from "@/components/site/LatestEntries";
import { FeaturedPodcast } from "@/components/site/FeaturedPodcast";
import { PlayedTracks } from "@/components/site/PlayedTracks";
import { ShareNowPlaying } from "@/components/site/ShareNowPlaying";
import { FavoriteTracks } from "@/components/site/FavoriteTracks";

const MoreLink = ({ to, testId, children }) => (
  <Link to={to} data-testid={testId} className="inline-flex items-center gap-3 text-white font-display uppercase text-xl hover:text-primary transition-colors">
    {children} <ArrowRight className="w-5 h-5" />
  </Link>
);

const LiveBlock = () => {
  const { playing, loading, toggle, nowPlaying, cover } = useRadio();
  return (
    <section className="relative pt-40 pb-20 md:pb-28 border-b border-white/10 brand-glow grain overflow-hidden" data-testid="radio-live">
      <div className="absolute inset-0 bg-[#0a0a0a]/45" />
      <Container className="relative">
        <Label>Radio LGTDZ · En direct · 7/7 - 24/24</Label>
        <h1 className="uppercase leading-[0.92] text-[12vw] sm:text-7xl lg:text-8xl xl:text-9xl mt-6">
          <span className="block font-display text-white">La radio</span>
          <span className="block pl-[8%] font-thin-brand text-white">en continu</span>
        </h1>
        <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
          <button onClick={toggle} data-testid="radio-page-play-btn" aria-label={playing ? "Pause" : "Écouter la radio"}
            className="w-24 h-24 shrink-0 bg-primary text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors duration-200">
            {loading ? <Loader2 className="w-9 h-9 animate-spin" /> : playing ? <Pause className="w-9 h-9" /> : <Play className="w-9 h-9 ml-1" />}
          </button>
          {cover && <img src={cover} alt="" className="w-24 h-24 object-cover shrink-0 hidden sm:block" data-testid="radio-page-cover" />}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-300">{playing ? "En écoute" : "À l'antenne"}</p>
            <p className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl text-white mt-2 break-words" data-testid="radio-page-now-playing">
              {nowPlaying || "LGTDZ — le média, la radio"}
            </p>
            <ShareNowPlaying testId="radio-page-share" label="Partager ce titre"
              className="mt-4 h-10 px-4 border border-white/30 text-white text-[11px] uppercase tracking-[0.2em] font-bold hover:border-primary hover:text-primary" />
          </div>
        </div>
        <p className="text-zinc-200 text-base sm:text-lg leading-relaxed max-w-2xl mt-12">
          Sélections pointues, transmission détendue. Le lecteur reste actif pendant que tu navigues sur le site.
        </p>
      </Container>
    </section>
  );
};

const PlaylistBlock = () => (
  <section className="py-24 md:py-32" data-testid="radio-playlist">
    <Container className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20">
      <div>
        <SectionHeading top="Arrivages" bottom="en playlist" testId="radio-playlist-title" />
        <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-8 max-w-md">Les dernières entrées dans la playlist de la radio.</p>
        <div className="mt-10"><MoreLink to="/arrivages" testId="radio-all-arrivages-btn">Toute la playlist</MoreLink></div>
      </div>
      <LatestEntries count={8} testId="radio-latest-entries" />
    </Container>
  </section>
);

const PodcastsBlock = () => (
  <section id="podcasts" className="py-24 md:py-32 border-t border-white/10 scroll-mt-20" data-testid="radio-podcasts">
    <Container>
      <SectionHeading top="Podcasts" bottom="en réécoute" outline testId="radio-podcasts-title" />
      <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-8 max-w-2xl">
        Sets, concerts captés en live, rap talks et premières radiophoniques de la communauté LGTDZ.
      </p>
    </Container>
    <ArticleList path="/podcasts" prefix="podcasts" filter={PODCAST_FILTER} />
  </section>
);

export default function Radio() {
  return (
    <div data-testid="radio-page">
      <LiveBlock />
      <FeaturedPodcast />
      <PlayedTracks />
      <FavoriteTracks />
      <PlaylistBlock />
      <PodcastsBlock />
    </div>
  );
}
